<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Assignment Details Box -->
            <div class="border border-dark p-4 mb-4 text-sm rounded" v-if="displayAssignmentDetails">
              <div class="mb-2">
                <strong>LOCATION:</strong> {{ displayAssignmentDetails.location }}
              </div>
              <div class="mb-2">
                <strong>YOUR EMAIL:</strong> {{ displayAssignmentDetails.email }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP:</strong> {{ displayAssignmentDetails.assignmentGroup }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP ID:</strong> {{ displayAssignmentDetails.assignmentGroupId }}
              </div>
              <div class="mb-0">
                <strong>ASSIGNMENT DATE:</strong> {{ displayAssignmentDetails.assignmentDate }}
              </div>
            </div>

            <!-- User Message -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
              <!-- Unavailable Notice -->
              <div v-if="unavailable" class="alert alert-warning d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">info</i>
                <span>{{ unavailableMessage }}</span>
              </div>
              <!-- Show confirmation result based on actual API response -->
              <div v-else-if="confirmationFailed" class="alert alert-danger d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">error</i>
                <span>{{ confirmationError }}</span>
              </div>
              <div v-else-if="confirmationProcessed" class="alert alert-success d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">check_circle</i>
                <span>{{ confirmationResponseMsg }}</span>
              </div>
              <p v-else class="mb-2 text-success fw-bold">{{ displayMessage }}</p>
              <p class="mb-2" v-if="displayAdditionalMessage">{{ displayAdditionalMessage }}</p>
              <p class="mb-0">Thank you.</p>
            </div>


            <!-- Divider -->
            <hr class="my-4" />

            <!-- Did You Know Section -->
            <div class="mb-4" v-if="showDidYouKnow">
              <h5 class="fw-bold mb-3">Did You Know?</h5>
              <p class="mb-3">{{ displayDidYouKnowText }}</p>
              <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
              </ul>
              <p class="mb-0" v-if="displaySmsInfo">{{ displaySmsInfo }}</p>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton color="success">
                Click here to Login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const confirmationProcessed = ref(false)
const confirmationFailed = ref(false)
const confirmationResponseMsg = ref('')
const confirmationError = ref('')
const unavailable = ref(false)
const unavailableMessage = ref('')
const assignmentDetails = ref({
  location: '',
  email: '',
  assignmentGroup: '',
  assignmentGroupId: '',
  assignmentDate: ''
})
const userName = ref('')
const message = ref('')
const additionalMessage = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])
const smsInfo = ref('')

// Mock fallback data
const mockAssignmentDetails = {
  location: 'GS Test Church - Chicago main Branch',
  email: 'Konadu_A_882@yourchurchscheduling.com',
  assignmentGroup: 'Usher @ Sunday Morning Service - main',
  assignmentGroupId: '39434',
  assignmentDate: 'Dec 28, 2005 10:00:00 AM'
}
const mockUserName = 'Konadu'
const mockMessage = 'Your assignment has been successfully confirmed.'
const mockAdditionalMessage = 'The current assignment status is accepted'
const mockDidYouKnowText = 'You can redirect mobile text reminders to your email address by following the steps below:'
const mockDidYouKnowSteps = [
  "log into the application and click on the 'My Profile/Preferences' button from the Home page",
  "enter your email address (must be unique) into the 'SMS address' field on the details tab",
  "set 'Enable SMS/Text alerts' option to 'Yes'",
  "Save your changes"
]
const mockSmsInfo = "With sms/text alerts enabled, you will not need to login to respond to assignments. You will be able to respond yes or no to assignments by simply replying to the text or email reminders you receive days before the event date. Also, you will be able to send commands like 'schedule' or 'pending' to get your upcoming assignments or upcoming assignment status. Mobile text reminders are currently only available in the U.S. and Canada, other countries can use the 'SMS address' option to send to email addresses."

// Computed display values (prefer API, fallback to mock)
const displayAssignmentDetails = computed(() =>
  assignmentDetails.value && Object.values(assignmentDetails.value).some(v => v)
    ? assignmentDetails.value
    : mockAssignmentDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayMessage = computed(() => message.value || mockMessage)
const displayAdditionalMessage = computed(() => additionalMessage.value || mockAdditionalMessage)
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() =>
  (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps
)
const displaySmsInfo = computed(() => smsInfo.value || mockSmsInfo)

// Process confirmation (auto on mount)
const processConfirmation = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) return

  try {
    const response = await api.get(`/account/confirmassignment/cs/${encodedString}`)
    if (response.data?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = response.data.message || 'This assignment is no longer available.'
      return
    }
    const msg = response.data?.message || 'Assignment confirmed successfully.'
    confirmationResponseMsg.value = msg
    confirmationProcessed.value = true
    toast.success(msg, { position: 'top-right' })
  } catch (error) {
    console.error('Error processing assignment confirmation:', error)
    const errData = error.response?.data
    if (errData?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = errData.message || 'This assignment is no longer available.'
      return
    }
    const errMsg = errData?.message || error.message || 'Failed to process confirmation.'
    confirmationError.value = errMsg
    confirmationFailed.value = true
    toast.error(errMsg, { position: 'top-right' })
  }
}

// Fetch page details
const fetchConfirmationDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/confirmassignment-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.assignmentDetails) {
        assignmentDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          assignmentGroup: data.assignmentDetails.assignment_group || '',
          assignmentGroupId: data.assignmentDetails.assignment_group_id || '',
          assignmentDate: data.assignmentDetails.assignment_date || ''
        }
      }

      userName.value = data.username || ''
      message.value = data.message || ''
      additionalMessage.value = data.additionalMessage || ''
      // Only surface an 'unavailable' notice from the details API when the
      // confirmation step itself has not already succeeded or failed. After a
      // successful confirm, the details endpoint naturally reports the
      // assignment as no longer pending/available, which is expected and
      // should NOT be shown as a warning on top of the success message.
      if (
        data.unavailable &&
        !unavailable.value &&
        !confirmationProcessed.value &&
        !confirmationFailed.value
      ) {
        unavailable.value = true
        unavailableMessage.value = data.message || 'This assignment is no longer available.'
      }
      if (data.showDidYouKnow != null) showDidYouKnow.value = data.showDidYouKnow
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
      if (data.smsInfo != null) smsInfo.value = data.smsInfo
    }
  } catch (error) {
    console.error('Error fetching confirmation details:', error)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await processConfirmation()
  await fetchConfirmationDetails()
})
</script>

